import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  HouseSimple,
  Sparkle,
  User as UserIcon,
  Vault,
  VideoCamera,
  SignOut,
  Shield,
  List,
  X as XIcon,
} from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";

/**
 * MobileNav — álló mobilnézetben (viewport < 768px) elérhető hamburger +
 * off-canvas drawer. A desktop Sidebar `hidden md:flex`, ezért a mobil
 * user korábban CSAK fekvő módban látta a menüt. Ez a komponens biztosítja,
 * hogy álló módban is elérhető legyen a fő menü, a user infó és a Kilépés.
 *
 * A top bar `md:hidden` — desktopon nem jelenik meg. A drawer teljes
 * képernyős, jobbról érkezik, a Kilépés gomb a legalján fix.
 */

const navItems = [
  { to: "/dashboard", label: "Műhely", icon: HouseSimple, testid: "mnav-studio" },
  { to: "/agents", label: "Ügynökök", icon: Sparkle, testid: "mnav-agents" },
  { to: "/identity", label: "Identitás", icon: UserIcon, testid: "mnav-identity" },
  { to: "/asset-vault", label: "Tudástár", icon: Vault, testid: "mnav-vault" },
  { to: "/recordings", label: "Felvételek", icon: VideoCamera, testid: "mnav-recordings" },
];

const adminNavItem = {
  to: "/admin",
  label: "Admin pult",
  icon: Shield,
  testid: "mnav-admin",
};

export default function MobileNav() {
  const { user, logout, effectiveRole } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const isAdmin = effectiveRole === "admin";
  const items = isAdmin ? [...navItems, adminNavItem] : navItems;

  // Auto-close a drawer minden route váltáskor — így nem marad nyitva
  // amikor a user átnavigál egy menüpontra.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Body scroll lock + ESC bezárás.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      {/* Top bar — csak mobilon (md:hidden). Fix a viewport tetején. */}
      <div
        className="md:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-3 border-b"
        data-testid="mobile-top-bar"
        style={{ background: "#0b0b12", borderColor: "rgba(255,255,255,0.06)" }}
      >
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="mobile-logo">
          <div className="relative w-8 h-8 rounded-lg bg-gradient-to-br from-[#5e50f7] to-[#3a2fd3] flex items-center justify-center shadow-[0_4px_20px_rgba(94,80,247,0.45)]">
            <Sparkle size={17} weight="fill" color="#fff" />
          </div>
          <div className="font-heading text-[17px] tracking-tight text-white">
            MEX <span className="text-[#5e50f7]">Magic</span>
          </div>
        </Link>
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="mobile-menu-btn"
          aria-label="Menü megnyitása"
          className="w-10 h-10 rounded-lg flex items-center justify-center transition"
          style={{
            background: "rgba(94,80,247,0.10)",
            border: "1px solid rgba(94,80,247,0.30)",
            color: "#c8c1ff",
          }}
        >
          <List size={20} weight="bold" />
        </button>
      </div>

      {/* Drawer + backdrop portal — a body-ba renderelve, hogy semmilyen
          stacking context ne rontsa el a z-indexet. */}
      {typeof document !== "undefined" && createPortal(
        <AnimatePresence>
          {open && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                onClick={() => setOpen(false)}
                data-testid="mobile-drawer-backdrop"
                style={{
                  position: "fixed",
                  inset: 0,
                  backgroundColor: "rgba(8,8,12,0.55)",
                  backdropFilter: "blur(4px)",
                  WebkitBackdropFilter: "blur(4px)",
                  zIndex: 9998,
                }}
              />
              <motion.aside
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "tween", duration: 0.24, ease: "easeOut" }}
                data-testid="mobile-drawer"
                style={{
                  position: "fixed",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: "min(85vw, 320px)",
                  background: "#0b0b12",
                  borderLeft: "1px solid rgba(255,255,255,0.08)",
                  boxShadow: "-24px 0 60px -20px rgba(0,0,0,0.5)",
                  zIndex: 9999,
                  display: "flex",
                  flexDirection: "column",
                  color: "#e9e7f5",
                }}
              >
                {/* Header — logo + close */}
                <div
                  className="flex items-center justify-between px-5 py-4 border-b"
                  style={{ borderColor: "rgba(255,255,255,0.06)" }}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#5e50f7] to-[#3a2fd3] flex items-center justify-center">
                      <Sparkle size={17} weight="fill" color="#fff" />
                    </div>
                    <div className="font-heading text-[17px] tracking-tight text-white">
                      MEX <span className="text-[#5e50f7]">Magic</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-drawer-close"
                    aria-label="Menü bezárása"
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-[#a1a1a1] hover:text-white transition"
                    style={{ background: "rgba(255,255,255,0.04)" }}
                  >
                    <XIcon size={16} weight="bold" />
                  </button>
                </div>

                {/* Nav */}
                <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
                  <div className="px-3 py-1 text-[10.5px] tracking-[0.14em] uppercase font-semibold text-[#7c7c8a]">
                    Menü
                  </div>
                  {items.map((item) => {
                    const Icon = item.icon;
                    const active =
                      location.pathname === item.to ||
                      location.pathname.startsWith(item.to + "/");
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        data-testid={item.testid}
                        className={`flex items-center gap-3 px-3 py-3 rounded-lg text-[14px] transition-all ${
                          active
                            ? "bg-[#5e50f7]/12 text-white border border-[#5e50f7]/30"
                            : "text-[#c8c8d2] hover:text-white hover:bg-white/[0.04] border border-transparent"
                        }`}
                      >
                        <Icon size={19} weight={active ? "fill" : "regular"} />
                        {item.label}
                      </NavLink>
                    );
                  })}
                </nav>

                {/* User + Kilépés — fix a legalján */}
                <div
                  className="p-4 border-t"
                  style={{ borderColor: "rgba(255,255,255,0.06)" }}
                >
                  <div className="flex items-center gap-3 px-2 py-2 mb-2">
                    {user?.picture ? (
                      <img
                        src={user.picture}
                        alt={user.name}
                        className="w-10 h-10 rounded-full object-cover border border-white/10"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#1a1a1a] border border-white/10 flex items-center justify-center text-[15px] font-semibold text-[#c8c1ff]">
                        {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || "?"}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[13.5px] font-medium truncate text-white" data-testid="mobile-drawer-username">
                        {user?.name || "Vendég"}
                      </div>
                      <div className="text-[11.5px] text-[#a1a1a1] truncate">{user?.email}</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={logout}
                    data-testid="mobile-logout-btn"
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-[14px] transition hover:-translate-y-0.5"
                    style={{
                      background: "rgba(251,113,133,0.12)",
                      border: "1px solid rgba(251,113,133,0.32)",
                      color: "#fb7185",
                    }}
                  >
                    <SignOut size={17} weight="bold" />
                    Kilépés
                  </button>
                </div>
              </motion.aside>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
